import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Menu, X, User, LogOut, Settings, FileText, Calendar, Users, Home, PenLine, Globe, CalendarDays, Image } from 'lucide-react';
import axios from 'axios';
import { useBranding } from '../context/BrandingContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Navbar = ({ variant = 'default' }) => {
  const { user, logout, isAdmin } = useAuth();
  const { language, toggleLanguage, t } = useLanguage();
  const { siteName, logoUrl } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isCompiled, setIsCompiled] = useState(false);
  
  const isDark = variant === 'dark';
  
  useEffect(() => {
    // Check if constitution is compiled
    const checkCompilationStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/signatures/status`);
        setIsCompiled(response.data.is_compiled || false);
      } catch (error) {
      }
    };
    checkCompilationStatus();
  }, []);
  
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Build nav links dynamically based on compilation status
  const navLinks = [
    { href: '/', label: t('nav.home'), icon: Home },
    { href: '/associations', label: t('nav.associations'), icon: Users },
    isCompiled
      ? { href: '/calendar', label: t('nav.events') || 'Events', icon: CalendarDays }
      : { href: '/calendar', label: t('nav.calendar'), icon: Calendar },
    ...(!isCompiled ? [{ href: '/sign', label: t('nav.sign'), icon: PenLine }] : []),
    { href: '/gallery', label: 'Gallery', icon: Image },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className={`sticky top-0 z-50 ${isDark ? 'glass-dark' : 'bg-white/80 backdrop-blur-xl border-b border-border'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            {logoUrl ? (
              <img src={logoUrl} alt={siteName} className="w-10 h-10 rounded-lg object-contain" data-testid="navbar-logo-img" />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cameroon-green via-cameroon-red to-cameroon-yellow flex items-center justify-center">
                <span className="text-white font-serif font-bold text-lg">C</span>
              </div>
            )}
            <span className={`font-serif font-bold text-xl ${isDark ? 'text-white' : 'text-foreground'}`}>
              {siteName}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={`flex items-center gap-2 text-sm font-medium transition-colors hover:text-primary ${
                  isActive(link.href)
                    ? 'text-primary'
                    : isDark ? 'text-white/80' : 'text-muted-foreground'
                }`}
              >
                <link.icon className="w-4 h-4" />
                {link.label}
              </Link>
            ))}
          </div>

          {/* Auth Section & Language Toggle */}
          <div className="hidden md:flex items-center gap-3">
            {/* Language Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className={`gap-2 ${isDark ? 'text-white hover:bg-white/10' : ''}`}
              data-testid="language-toggle"
            >
              <Globe className="w-4 h-4" />
              <span className="font-medium">{language === 'en' ? 'FR' : 'EN'}</span>
            </Button>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className={`gap-2 ${isDark ? 'text-white hover:bg-white/10' : ''}`} data-testid="user-menu-btn">
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                      <span className="text-primary-foreground text-sm font-medium">
                        {user.full_name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="hidden lg:block">{user.full_name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user.full_name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                    <p className="text-xs text-primary capitalize mt-1">{user.role}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-link">
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </DropdownMenuItem>
                  {isAdmin() && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-link">
                      <Settings className="w-4 h-4 mr-2" />
                      {t('admin.sidebar.dashboard')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button onClick={() => navigate('/login')} variant="ghost" className={isDark ? 'text-white hover:bg-white/10' : ''} data-testid="login-btn">
                {t('nav.login')}
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-2 md:hidden">
            {/* Mobile Language Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className={isDark ? 'text-white' : ''}
              data-testid="language-toggle-mobile"
            >
              <Globe className="w-4 h-4" />
            </Button>
            
            <button
              className={`p-2 ${isDark ? 'text-white' : 'text-foreground'}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className={`md:hidden py-4 border-t ${isDark ? 'border-white/10' : 'border-border'}`}>
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive(link.href)
                      ? 'bg-primary/10 text-primary'
                      : isDark ? 'text-white/80 hover:bg-white/10' : 'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <link.icon className="w-4 h-4" />
                  {link.label}
                </Link>
              ))}
              
              {user ? (
                <>
                  <Link
                    to="/profile"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isDark ? 'text-white/80 hover:bg-white/10' : 'text-muted-foreground hover:bg-muted'}`}
                  >
                    <User className="w-4 h-4" />
                    Profile
                  </Link>
                  
                  {isAdmin() && (
                    <Link
                      to="/admin"
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isDark ? 'text-white/80 hover:bg-white/10' : 'text-muted-foreground hover:bg-muted'}`}
                    >
                      <Settings className="w-4 h-4" />
                      {t('admin.sidebar.dashboard')}
                    </Link>
                  )}
                  
                  <button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-destructive transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-muted'}`}
                  >
                    <LogOut className="w-4 h-4" />
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <Button 
                  variant="ghost" 
                  onClick={() => { navigate('/login'); setMobileMenuOpen(false); }} 
                  className="justify-start"
                >
                  {t('nav.login')}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
